import { inject, Injectable, signal, computed } from '@angular/core';
import { 
  StatisticsAiService, 
  StatsAiTeamVideosRequest, 
  StatsAiStatsRequest, 
  StatsAiOverviewDTO, 
  PaginatedTeamVideosDTO,
  StatisticsService,
  TeamInfoDTO,
  Season
} from '@solverml/api';

@Injectable({
  providedIn: 'root'
})
export class StatsAiStateService {
  private readonly statsAiApi = inject(StatisticsAiService);
  private readonly statsApi = inject(StatisticsService);

  // Estado interno (signal)
  private readonly _teamInfo = signal<TeamInfoDTO | null>(null);
  private readonly _seasons = signal<Season[] | null>(null);
  private readonly _teamVideos = signal<PaginatedTeamVideosDTO | null>(null);
  private readonly _statsOverview = signal<StatsAiOverviewDTO | null>(null);
  private readonly _opponentStatsOverview = signal<StatsAiOverviewDTO | null>(null);
  private readonly _selectedVideoIds = signal<string[]>([]);
  private readonly _effectiveVideoIds = signal<string[]>([]);
  private readonly _opponentTeamName = signal<string | null>(null);
  private readonly _isLoading = signal<boolean>(false);
  private readonly _currentTeamId = signal<string | null>(null);
  private readonly _currentSeasonId = signal<number | null>(null);
  
  // Selectores públicos (computed / readonly signals)
  readonly teamInfo = this._teamInfo.asReadonly();
  readonly seasons = this._seasons.asReadonly();
  readonly teamVideos = this._teamVideos.asReadonly();
  readonly statsOverview = this._statsOverview.asReadonly();
  readonly opponentStatsOverview = this._opponentStatsOverview.asReadonly();
  readonly selectedVideoIds = this._selectedVideoIds.asReadonly();
  readonly effectiveVideoIds = this._effectiveVideoIds.asReadonly();
  readonly opponentTeamName = this._opponentTeamName.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly currentTeamId = this._currentTeamId.asReadonly();
  readonly currentSeasonId = this._currentSeasonId.asReadonly();

  readonly hasSelectedVideos = computed(() => this._selectedVideoIds().length > 0);

  // Acciones
  async loadInitialData(teamId: string): Promise<void> {
    this._isLoading.set(true);
    this._currentTeamId.set(teamId);
    try {
      const [teamInfo, seasons] = await Promise.all([
        this.statsApi.getTeamInfoEndpoint(teamId).toPromise(),
        this.statsApi.getSeasonsEndpoint().toPromise()
      ]);
      
      this._teamInfo.set(teamInfo || null);
      this._seasons.set(seasons || null);

      // En IA la temporada es opcional por defecto.
      this._currentSeasonId.set(null);
      await this.loadTeamVideos(teamId, null, 1);
    } catch (error) {
      console.error('Error loading initial team data:', error);
      this._teamInfo.set(null);
      this._seasons.set(null);
    } finally {
      this._isLoading.set(false);
    }
  }

  async loadTeamVideos(teamId: string, seasonId: number | null = null, page: number = 1): Promise<void> {
    this._isLoading.set(true);
    this._currentTeamId.set(teamId);
    this._currentSeasonId.set(seasonId);
    
    try {
      const request: StatsAiTeamVideosRequest = {
        team_id: teamId,
        page,
        page_size: 20
      };
      if (seasonId) {
        request.season_id = seasonId;
      }
      
      const response = await this.statsAiApi.getTeamVideos(request).toPromise();
      if (response) {
        this._teamVideos.set(response);
      } else {
        this._teamVideos.set(null);
      }
    } catch (error) {
      console.error('Error loading team videos:', error);
      this._teamVideos.set(null);
    } finally {
      this._isLoading.set(false);
    }
  }

  async loadStats(filters: Partial<StatsAiStatsRequest> = {}): Promise<void> {
    const teamId = this._currentTeamId();
    const videoIds = this._selectedVideoIds();
    const effectiveVideoIds = (filters.video_ids && filters.video_ids.length > 0)
      ? filters.video_ids
      : videoIds;
    
    if (!teamId || effectiveVideoIds.length === 0) return;

    this._isLoading.set(true);
    this._effectiveVideoIds.set(effectiveVideoIds);
    this._opponentStatsOverview.set(null);
    this._opponentTeamName.set(null);
    try {
      const request: StatsAiStatsRequest = {
        team_id: teamId,
        video_ids: effectiveVideoIds,
        ...filters
      };
      
      const response = await this.statsAiApi.getTeamStats(request).toPromise();
      if (response) {
        this._statsOverview.set(response);
      } else {
        this._statsOverview.set(null);
      }

      await this.loadOpponentStatsForSingleVideo(teamId, effectiveVideoIds, filters);
    } catch (error) {
      console.error('Error loading AI stats:', error);
      this._statsOverview.set(null);
      this._opponentStatsOverview.set(null);
      this._opponentTeamName.set(null);
    } finally {
      this._isLoading.set(false);
    }
  }

  toggleVideoSelection(videoId: string): void {
    this._selectedVideoIds.update(ids => {
      if (ids.includes(videoId)) {
        return ids.filter(id => id !== videoId);
      }
      return [...ids, videoId];
    });
  }

  selectAllVideos(): void {
    const data = this._teamVideos()?.data;
    if (data) {
      this._selectedVideoIds.set(data.map(v => v.video_id));
    }
  }

  clearSelection(): void {
    this._selectedVideoIds.set([]);
    this._effectiveVideoIds.set([]);
    this._statsOverview.set(null);
    this._opponentStatsOverview.set(null);
    this._opponentTeamName.set(null);
  }

  clearState(): void {
    this._teamInfo.set(null);
    this._seasons.set(null);
    this._teamVideos.set(null);
    this._statsOverview.set(null);
    this._opponentStatsOverview.set(null);
    this._selectedVideoIds.set([]);
    this._effectiveVideoIds.set([]);
    this._opponentTeamName.set(null);
    this._currentTeamId.set(null);
    this._currentSeasonId.set(null);
    this._isLoading.set(false);
  }

  private async loadOpponentStatsForSingleVideo(
    currentTeamId: string,
    effectiveVideoIds: string[],
    filters: Partial<StatsAiStatsRequest>
  ): Promise<void> {
    if (effectiveVideoIds.length !== 1) {
      return;
    }

    const selectedVideoId = effectiveVideoIds[0];
    const video = this._teamVideos()?.data?.find(v => v.video_id === selectedVideoId);
    const opponentTeamId = video?.opponent_team_id;
    const opponentTeamName = video?.opponent_team_name || null;

    if (!opponentTeamId || opponentTeamId === currentTeamId) {
      return;
    }

    const opponentRequest: StatsAiStatsRequest = {
      ...filters,
      team_id: opponentTeamId,
      video_ids: effectiveVideoIds,
      opponent_team_id: currentTeamId
    };

    try {
      const opponentResponse = await this.statsAiApi.getTeamStats(opponentRequest).toPromise();
      this._opponentStatsOverview.set(opponentResponse || null);
      this._opponentTeamName.set(opponentResponse ? opponentTeamName : null);
    } catch (error) {
      console.error('Error loading AI opponent stats:', error);
      this._opponentStatsOverview.set(null);
      this._opponentTeamName.set(null);
    }
  }
}
